window.Exh = window.Exh || {};

class ExhBuy extends HTMLElement {

  static observedAttributes = [
    "data-variant-id",
    "data-plan-id",
    "data-plan-type",
  ];

  constructor() {
    super();
    this._product = null;
    this._options = [];
  }

  connectedCallback() {
    this.productForm = document.forms[this.getAttribute("form")];
    this.addEventListener('option:change', this.handleOptionChange);
    this.addEventListener('plan:change', this.handlePlanChange);
    this.addEventListener('plan-type:change', this.handlePlanTypeChange);
    this.renderOptionValues();
  }

  disconnectedCallback() {
    this.removeEventListener('option:change', this.handleOptionChange);
    this.removeEventListener('plan:change', this.handlePlanChange);
    this.removeEventListener('plan-type:change', this.handlePlanTypeChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.render();
  }

  render() {
    this.initOptions();
    this.setInputs();
    this.setSellingPlans();
    this.setUrlParams();
    this.renderOptionValues();
    this.renderSummary();
  }

  initOptions() {
    if (this.options.length) return;
    let values = this.variant.options;
    this._options = this.product.options.map((name, i) => {
      return { name, value: values[i] }
    });
  }

  setInputs() {
    this.idInputs.forEach(input => input.value = this.variantId);
    this.sellingPlanInputs.forEach(input => input.value = this.isSubscription() ? this.planId : '');
  }

  isSubscription() {
    return this.planType === this.RECURRING;
  }

  setSellingPlans() {
    this.querySelectorAll('exh-selling-plans').forEach(el => {
      el.dataset.variantId = this.variantId;
    });
  }

  setUrlParams() {
    this.updateUrlParam('variant', this.variantId);
    this.updateUrlParam('selling_plan', this.planId);
  }

  updateUrlParam(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    if (!Shopify.designMode) {
      history.replaceState({}, '', url);
    }
  }

  get RECURRING() {
    return 'recurring';
  }

  get planPrice() {
    return this.variant?.selling_plan_allocations.find(a => a.selling_plan_id == this.planId)?.price || '';
  }

  renderOptionValues() {
    let options = this.options;
    let availableVariantOptionValues = this.variants.filter(v => v.available).map(v => v.options);

    this.querySelectorAll('exh-variant-option').forEach(optionValue => {
      let { name, value } = optionValue.dataset;
      let active = this.optionIsActive(options, name, value);

      let otherOptions = options.filter(o => o.name !== name);

      let available = availableVariantOptionValues.some((values) => {
        return this.optionIsAvailable(values, otherOptions, value);
      });

      optionValue.classList.toggle('active', active);
      optionValue.classList.toggle('available', available);
    });
  }

  optionIsActive(options, name, value) {
    return options.some(option => option.name === name && option.value === value);
  }

  optionIsAvailable(values, otherOptions, value) {
    return values.includes(value) && otherOptions.every(o => values.includes(o.value));
  }

  renderSummary() {
    let summaryEl = this.querySelector('.summary');
    if (!summaryEl) return;
    let variant = this.variant;
    let price = variant.price;
    let compare_at_price = (variant.compare_at_price > price) ? variant.compare_at_price : '';
    let plan_price = (this.isSubscription() && price > this.planPrice) ? this.planPrice : '';
    let html = `
    <div class="summary-variant">
      ${this.renderOption(variant.option1 || '', 'option-1')}
      ${this.renderOption(variant.option2 || '', 'option-2')}
      ${this.renderOption(variant.option3 || '', 'option-3')}
    </div>
    <div class="summary-prices">
      ${this.renderPrice(compare_at_price, 'compare_at_price')}
      ${this.renderPrice(price, 'price')}
      ${this.renderPrice(plan_price, 'plan_price')}
    </div>
    `;
    summaryEl.innerHTML = html;
  }

  renderOption(option, classes) {
    return option && `<span class="${classes}">${option}</span>`;
  }

  renderPrice(price, classes) {
    return price && `<span class="${classes}">${this.formatMoney(price)}</span>`;
  }

  formatMoney(value) {
    return window.Exh.formatMoney(value)
  }

  handleTheme(product, variant, previousVariant) {
    this.productForm.querySelectorAll(`input[name="id"]`).forEach(input => {
      input.value = variant.id;
    });

    this.productForm.dispatchEvent(new CustomEvent("variant:change", {
      bubbles: true,
      detail: {
        product,
        variant,
        previousVariant
      }
    }));
  }

  handleOptionChange(event) {
    event.stopPropagation();
    let previousVariant = this.variant;

    let { name, value } = event.detail;
    this.options = this.options.map(option => {
      option.value = (option.name === name) ? value : option.value;
      return option;
    });

    if (previousVariant.id !== this.variantId) {
      this.handleTheme(this.product, this.variant, previousVariant);
    };
  }

  handlePlanChange(event) {
    event.stopPropagation();
    this.planId = event.detail;
  }

  handlePlanTypeChange(event) {
    event.stopPropagation();
    this.planType = event.detail;
  }

  get variantId() {
    return parseInt(this.dataset.variantId);
  }

  set variantId(id) {
    this.dataset.variantId = id;
  }

  get planId() {
    return parseInt(this.dataset.planId);
  }

  set planId(id) {
    this.dataset.planId = id;
  }

  get planType() {
    return this.dataset.planType;
  }

  set planType(value) {
    this.dataset.planType = value;
  }

  get product() {
    if (this._product === null) {
      this._product = JSON.parse(this.querySelector('script').innerText);
    }
    return this._product;
  }

  get variants() {
    return this.product.variants || [];
  }

  get groups() {
    return this.product.selling_plan_groups || [];
  }

  get plans() {
    return this.groups[0].selling_plans || [];
  }

  get idInputs() {
    return this.querySelectorAll('input[name="id"]');
  }

  get sellingPlanInputs() {
    return this.querySelectorAll('input[name="selling_plan"]');
  }

  get options() {
    return this._options;
  }

  set options(options) {
    this._options = options;
    this.variantId = this.findSelectedVariant(options)?.id;
  }

  get variant() {
    return this.findVariantById(this.variantId);
  }

  get plan() {
    return findPlanById(this.planId);
  }

  findPlanById(id) {
    return this.plans.find(p => p.id === id) || {};
  }

  findVariantById(id) {
    return this.variants.find(v => v.id === id);
  }

  findFirstAvailableVariant() {
    return this.variants.find(v => v.available);
  }

  findSelectedVariant(options) {
    options = options || this.options || [];
    return this.variants.find(v => {
      return v.option1 == options[0]?.value && v.option2 == options[1]?.value && v.option3 == options[2]?.value
    });
  }

}

customElements.define('exh-buy', ExhBuy);

class ExhVariantOption extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    let { id, name, value } = this.dataset;
    this.dispatchEvent(new CustomEvent("option:change", { bubbles: true, detail: { id, name, value } }));
  }
}

customElements.define('exh-variant-option', ExhVariantOption);
